import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Briefcase, IndianRupee, Clock, ShieldCheck, Building2,
  ExternalLink, Star, Bookmark, BookmarkCheck,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────
 *  <JobCard /> — AmbitionBox-style dense job card.
 *
 *  Layout
 *  ──────
 *    [LOGO 56px]   Title (bold, 1-2 line clamp)  · rating (optional)  · Save
 *                  Company  ·  optional badges (External / Verified)
 *                  📍 Location  💼 Experience  💸 Salary
 *                  Skill chips (up to 4) + n more
 *                  ──────────────────────────────────────
 *                  Domain pill  ·  type   posted-time    View / Apply →
 * ──────────────────────────────────────────────────────────────────────── */

const SC_TINT = {
  SMPL: "#F59E0B", EVFM: "#10B981", MEDL: "#DC2626",
  AIOF: "#9333EA", SMIA: "#0EA5E9", CIDSA: "#16A34A",
  ESDM: "#F97316", "3CI": "#EF4444", READ: "#0891B2",
  "IoT-CPS": "#7C3AED", OTHER: "#64748B",
};

function timeAgo(value) {
  if (!value) return "";
  try {
    const ms = typeof value === "number" ? value * 1000 : Date.parse(value);
    if (!ms || isNaN(ms)) return "";
    const diff = Math.floor((Date.now() - ms) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
    return `${Math.floor(diff / 2592000)}mo ago`;
  } catch { return ""; }
}

function CompanyLogo({ src, name, size = 56 }) {
  const [broken, setBroken] = React.useState(false);
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  if (src && !broken) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => setBroken(true)}
        className="rounded-md object-contain bg-white border border-rule shrink-0"
        style={{ width: size, height: size }}
        loading="lazy"
      />
    );
  }
  return (
    <div
      className="rounded-md border border-rule grid place-items-center bg-slate-50 shrink-0"
      style={{ width: size, height: size }}
    >
      <span className="text-lg font-bold text-slate-400" aria-hidden="true">{initial}</span>
      <span className="sr-only">{name}</span>
    </div>
  );
}

function expDisplay(p) {
  const lo = p.experience_min, hi = p.experience_max;
  if (lo == null && hi == null) return null;
  if (lo != null && hi != null) return `${lo}–${hi} Yrs`;
  if (lo != null) return `${lo}+ Yrs`;
  return `Up to ${hi} Yrs`;
}

export default function JobCard({
  p,
  ctaTo = "/login",
  ctaLabel = "Apply",
  onApply,           // optional apply handler (when student is logged in on /opportunities)
  onSave,            // optional save handler
  saved,             // boolean
  external = false,  // external job flag (from RemoteOK / Adzuna / Jooble)
  testid,
}) {
  const exp = expDisplay(p);
  const salary = p.salary_display || p.stipend || null;
  const skills = (p.skills_required || []).filter(Boolean);
  const visibleSkills = skills.slice(0, 4);
  const extraSkills = Math.max(0, skills.length - visibleSkills.length);
  const domainBadge = p.sc_domain
    ? { label: p.sc_domain_name || p.sc_domain, color: SC_TINT[p.sc_domain] || "#0040A8" }
    : null;
  const isOther = p.sc_domain === "OTHER";
  const rating = p.company_rating; // optional 0-5

  // Card wraps as a Link for internal, <a target=_blank> for external
  const CardShell = ({ children }) => external ? (
    <a
      href={p.external_url || "#"}
      target="_blank"
      rel="noopener noreferrer"
      className="block group bg-white border border-rule rounded-xl p-4 sm:p-5 transition hover:border-signal/40 hover:shadow-sm hover:-translate-y-0.5"
      data-testid={testid || `job-card-external-${p.source_id || p.id}`}
    >{children}</a>
  ) : (
    <Link
      to={`/jobs/${p.id}`}
      className="block group bg-white border border-rule rounded-xl p-4 sm:p-5 transition hover:border-signal/40 hover:shadow-sm hover:-translate-y-0.5"
      data-testid={testid || `job-card-${p.id}`}
    >{children}</Link>
  );

  return (
    <CardShell>
      <div className="flex gap-3 sm:gap-4">
        <CompanyLogo src={p.company_logo} name={p.company_name} />
        <div className="flex-1 min-w-0">
          {/* Title row + save button */}
          <div className="flex items-start gap-2">
            <h3
              className="font-display font-bold text-[15px] sm:text-base leading-snug line-clamp-2 flex-1 group-hover:text-signal transition"
              data-testid={`job-title-${p.id || p.source_id}`}
            >
              {p.title}
            </h3>
            {onSave && !external && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSave(p); }}
                className="shrink-0 text-muted hover:text-signal transition p-1 -m-1"
                aria-label={saved ? "Unsave job" : "Save job"}
                data-testid={`save-${p.id}`}
              >
                {saved ? <BookmarkCheck size={16} className="text-signal" /> : <Bookmark size={16} />}
              </button>
            )}
            {external && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-white bg-slate-500 px-1.5 py-0.5 rounded shrink-0">
                <ExternalLink size={9} strokeWidth={2.5} /> External
              </span>
            )}
          </div>

          {/* Company + optional rating */}
          <div className="flex items-center gap-2 mt-0.5 text-sm text-slate-600">
            <span className="truncate">{p.company_name || "Company"}</span>
            {rating != null && (
              <span className="inline-flex items-center gap-0.5 text-xs text-slate-500 shrink-0">
                <Star size={11} className="fill-amber-400 text-amber-400" />
                {Number(rating).toFixed(1)}
              </span>
            )}
          </div>

          {/* Meta line — always fills to show at least location */}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-slate-700">
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <MapPin size={13} className="text-muted shrink-0" />
              <span className="truncate">{p.location || (p.remote ? "Remote" : "—")}</span>
            </span>
            {exp && (
              <span className="inline-flex items-center gap-1.5">
                <Briefcase size={13} className="text-muted" />
                {exp}
              </span>
            )}
            {salary && (
              <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
                <IndianRupee size={13} className="text-muted" />
                {salary}
              </span>
            )}
          </div>

          {/* Skill chips */}
          {visibleSkills.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {visibleSkills.map((s) => (
                <span
                  key={s}
                  className="text-[11px] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-700 truncate max-w-[140px]"
                  title={s}
                >
                  {s}
                </span>
              ))}
              {extraSkills > 0 && (
                <span className="text-[11px] font-semibold text-signal px-2 py-0.5">
                  +{extraSkills} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom row */}
      <div className="mt-3 pt-3 border-t border-rule flex items-center gap-2 flex-wrap">
        {domainBadge && (
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${isOther ? "border" : "text-white"}`}
            style={isOther
              ? { background: "#F1F5F9", color: domainBadge.color, borderColor: domainBadge.color + "55" }
              : { background: domainBadge.color }}
          >
            <ShieldCheck size={9} /> {domainBadge.label}
          </span>
        )}
        {p.type && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border border-rule text-slate-600">
            {p.type}
          </span>
        )}
        {(p.remote && external) && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border border-rule text-slate-600">
            Remote
          </span>
        )}
        <span className="text-[11px] text-muted inline-flex items-center gap-1 ml-auto">
          <Clock size={11} /> {timeAgo(p.created_at || p.posted_at) || "recent"}
        </span>
        {external ? (
          <span
            className="text-xs font-semibold text-signal inline-flex items-center gap-1 group-hover:underline"
            data-testid={`view-external-${p.source_id || p.id}`}
          >
            Apply <ExternalLink size={11} />
          </span>
        ) : (
          <span
            className="text-xs font-semibold text-signal inline-flex items-center gap-1 group-hover:underline"
            data-testid={`view-details-${p.id}`}
          >
            View →
          </span>
        )}
      </div>

      {/* Optional inline apply — used from student dashboard when we want a quick apply */}
      {onApply && !external && (
        <div className="mt-3 pt-3 border-t border-rule flex justify-end">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onApply(p); }}
            className="text-sm font-semibold px-4 py-1.5 rounded-md text-white shadow-sm hover:shadow-md transition"
            style={{ background: "linear-gradient(135deg, #006CFF 0%, #0040A8 100%)" }}
            data-testid={`apply-${p.id}`}
          >
            {ctaLabel}
          </button>
        </div>
      )}
      {/* Fallback external CTA when a non-student anon browses the public feed */}
      {!onApply && !external && ctaTo && (
        <span className="sr-only">{ctaLabel}</span>
      )}
    </CardShell>
  );
}
